import { useEffect, useState, useRef, useMemo } from "react";
import {
  Badge,
  Box,
  Button,
  Divider,
  Paper,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { useUser } from "../../../store/user/hooks";
import { useUsers } from "../../../store/users/hooks";
import {
  DataGridPro,
  useGridApiRef,
  Toolbar,
  ToolbarButton,
  ColumnsPanelTrigger,
  FilterPanelTrigger,
} from "@mui/x-data-grid-pro";
import UsersCounter from "../../../components/users/UsersCounter";
import { Add, FilterList, ViewColumn } from "@mui/icons-material";
import { hasAdminRole } from "../../../store/AclUtil";
import { formatDate, humanFileSize } from "../../../store/formatUtil";
import { useNavigate } from "react-router-dom";
import { ContentTitleBar } from "../../../components/ContentTitleBar";
import { WaitDialog } from "../../../components/dialogs";
import {
  buildDriveUsageRequestUser,
  driveUsageBytes,
  DRIVE_USAGE,
  DRIVE_USAGE_TIMEOUT_MS,
  USER_SCOPE,
} from "../utils/driveUsage";
import useWebSocket from "react-use-websocket";
import { WEBSOCKET_URL } from "../../../store/config";
import { useAuth } from "../../../store/auth/hooks";
import { LoadingDots } from "../../../components/LoadingDots";

export default function ListUsersPage() {
  const theme = useTheme();
  const gridRef = useGridApiRef();
  const navigate = useNavigate();
  const { loadUsers, isLoading, list } = useUsers();
  const user = useUser();
  const { accessCode } = useAuth();
  const [websocketRegistered, setWebsocketRegistered] = useState(false);
  // On-disk size per clip, keyed by clip id. Kept apart from caseClips because
  // a CLIP_STATUS message reloads that list wholesale, which would throw the
  // measurements away.
  const [driveUsage, setDriveUsage] = useState({});
  const [measuringDriveUsage, setMeasuringDriveUsage] = useState(false);
  // The correlation id of the batch we asked for. Drive usage messages fan out
  // to every session this user has open, so this is the only way to tell our
  // own batch's terminator from another tab's.
  const driveUsageCorrelationId = useRef(null);

  const isEnabled = (u) => {
    if (u === undefined) return false;
    return u;
  };

  const CustomToolbar = () => {
    return (
      <Toolbar>
        <Tooltip title="Filters">
          <FilterPanelTrigger
            sx={{
              color: theme.palette.primary.dark,
            }}
            render={(props, state) => (
              <ToolbarButton {...props} color="default">
                <Badge badgeContent={state.filterCount} color="primary">
                  <FilterList fontSize="small" />
                </Badge>
              </ToolbarButton>
            )}
          />
        </Tooltip>
        <Tooltip title="Columns">
          <ColumnsPanelTrigger
            sx={{
              color: theme.palette.primary.dark,
            }}
            render={<ToolbarButton />}
          >
            <ViewColumn fontSize="small" />
          </ColumnsPanelTrigger>
        </Tooltip>
      </Toolbar>
    );
  };

  const columns = [
    { field: "firstName", headerName: "First Name", flex: 1 },
    { field: "lastName", headerName: "Last Name", flex: 1 },
    {
      field: "organization",
      headerName: "Organization",
      flex: 1,
      valueFormatter: (value) => (value === "" ? "---" : value),
    },
    { field: "email", headerName: "Email", flex: 2 },
    {
      field: "phoneNumber",
      headerName: "Phone",
      flex: 1,
      valueFormatter: (value) => {
        // 1. Handle literal null or undefined
        if (value == null) return "---";

        // 2. Safely cast to string and clean it up
        const valStr = String(value).trim();

        // 3. Handle literal string "null" or empty strings
        if (valStr.toLowerCase() === "null" || valStr === "") {
          return "---";
        }

        // 4. Strip out "null" if it is embedded inside a string (e.g., "+1555null234")
        const cleaned = valStr.replace(/null/gi, "").trim();
        return cleaned === "" ? "---" : cleaned;
      },
    },
    {
      field: "roles",
      headerName: "Role",
      flex: 0.5,
      valueFormatter: (value) =>
        hasAdminRole({ roles: value }) ? "ADMIN" : "USER",
    },
    {
      field: "enabled",
      headerName: "Status",
      flex: 0.5,
      valueFormatter: (value) => (isEnabled(value) ? "ENABLED" : "DISABLED"),
    },
    {
      field: "lastLoginDate",
      headerName: "Last Login",
      flex: 1,
      type: "date",
      // 1. Convert incoming row values to a valid Date object or empty string.
      // Returning "" skips the type: "date" parser crashes.
      valueGetter: (value) => {
        if (value === 0 || value === "0" || !value) {
          return "";
        }
        const parsedDate = new Date(value);
        return !isNaN(parsedDate.getTime()) ? parsedDate : "";
      },
      // 2. Format the value provided by valueGetter.
      // v8 passes the parsed value directly as the first argument.
      valueFormatter: (value) => {
        if (value instanceof Date) {
          return formatDate(value.getTime());
        }
        return "---";
      },
    },
    {
      field: "directorySize",
      headerName: "Drive Usage",
      flex: 1,
      type: "number",
      align: "center",
      headerAlign: "center",
      renderCell: ({ value }) => (
        <Box
          display="flex"
          alignItems="center"
          justifyContent="center"
          height={1}
          width={1}
        >
          {value === undefined ? (
            // Still measuring, or the request never came back.
            measuringDriveUsage ? (
              <LoadingDots />
            ) : (
              <Typography variant="body2">&mdash;</Typography>
            )
          ) : (
            <Typography variant="body2">
              {value === null ? "—" : humanFileSize(value, true)}
            </Typography>
          )}
        </Box>
      ),
    },
  ];

  const navigateToAddUser = async () => {
    await user.reset();
    navigate("/app/admin/addUser");
  };

  const handleRowClick = (e) => {
    const navigateToUser = async (userId) => {
      await user.reset();
      navigate(`/app/admin/user/${userId}`);
    };

    //Navigate to User
    navigateToUser(e.id);
  };

  /**
   * Drive usage arrives one message per clip, then a terminator carrying
   * `complete: true`. Ordering is guaranteed server-side, so the terminator
   * can't overtake the results it closes out.
   */
  const handleDriveUsageMessage = (payload) => {
    if (!payload) return;
    const { correlationId, usage, complete, error } = payload;

    // Take any clip measurement, even from a correlation id we never issued:
    // the server also pushes updates unprompted when something changes on
    // disk, which saves the client from having to know which clip statuses
    // affect footprint. Clip ids are unique across cases, so a message meant
    // for another tab's case can't land on one of our rows.

    // TODO: change scope to user or case
    if (usage && usage.scope === USER_SCOPE) {
      setDriveUsage((prev) => ({ ...prev, [usage.id]: usage }));
    }

    // Only our own batch drives the loading state.
    if (!correlationId || correlationId !== driveUsageCorrelationId.current) {
      return;
    }
    if (error) {
      console.error("[UsersPage][WebSocket] Drive usage error:", error);
    }
    if (complete) {
      driveUsageCorrelationId.current = null;
      setMeasuringDriveUsage(false);
    }
  };

  const { sendMessage } = useWebSocket(WEBSOCKET_URL, {
    share: true,
    onOpen: () => {
      console.log("WebSocket connection established.", WEBSOCKET_URL);
    },
    onMessage: (event) => {
      if (event && event.data) {
        if (event.data === "pong") return;
        const data = JSON.parse(event.data);
        if (data.command === DRIVE_USAGE) {
          handleDriveUsageMessage(data.data);
        }
      }
    },
  });

  useEffect(() => {
    if (!websocketRegistered) {
      setWebsocketRegistered(true);
      const payload = {
        command: "REGISTER",
        authorization: accessCode,
      };
      sendMessage(JSON.stringify(payload));
    }
  }, [accessCode, websocketRegistered, sendMessage]);

  /**
   * Ask for drive usage once per user, after REGISTER has gone out -- only
   * registered sessions are in the server's cache, and answers to an
   * unregistered session go nowhere. This effect runs a render later than the
   * registration effect above, so the ordering holds.
   */
  useEffect(() => {
    if (!websocketRegistered || !list.length) return;

    const correlationId = crypto.randomUUID();
    driveUsageCorrelationId.current = correlationId;
    setDriveUsage({});
    setMeasuringDriveUsage(true);
    // NOTE: for user and case loop over each user/case and send a request for each user/case combination
    list.forEach((user) => {
      sendMessage(
        buildDriveUsageRequestUser(accessCode, user.id, correlationId),
      );
    });

    // A payload the server can't parse is dropped without a reply, so stop
    // waiting rather than animating forever.
    const timeout = setTimeout(() => {
      if (driveUsageCorrelationId.current === correlationId) {
        console.warn("[UsersPage] Drive usage request timed out");
        driveUsageCorrelationId.current = null;
        setMeasuringDriveUsage(false);
      }
    }, DRIVE_USAGE_TIMEOUT_MS);

    return () => clearTimeout(timeout);
  }, [accessCode, list, websocketRegistered, sendMessage]);

  useEffect(() => {
    // Fetch the users when the component is mounted
    loadUsers();
  }, [loadUsers]);

  const rows = useMemo(
    () =>
      list.map((user) => ({
        ...user,
        driveUsage: driveUsageBytes(driveUsage[user.id]),
      })),
    [list, driveUsage],
  );

  return (
    <Stack component="main" height={1}>
      <WaitDialog open={isLoading} />
      <Stack flex={1} p={2}>
        <Box display="flex" mb={1.5}>
          <UsersCounter value={list.length} />
        </Box>
        <Divider />
        <Stack height="calc(100vh - 260px)" maxHeight="calc(100vh - 260px)">
          <DataGridPro
            apiRef={gridRef}
            columns={columns}
            disableColumnMenu
            hideFooter
            hideFooterPagination
            isCellEditable={() => {
              return false;
            }}
            loading={isLoading}
            onRowClick={handleRowClick}
            rows={rows}
            showToolbar
            slots={{
              toolbar: CustomToolbar,
            }}
            slotProps={{
              columnsManagement: {
                disableResetButton: true,
              },
            }}
            sx={{
              border: "none",
            }}
          />
        </Stack>
      </Stack>
      <Paper
        elevation={4}
        sx={{
          p: 1,
          mx: 1,
          mb: 1,
        }}
      >
        <Button
          aria-label="Add User"
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigateToAddUser()}
        >
          Add User
        </Button>
      </Paper>
      <ContentTitleBar label="Users" />
    </Stack>
  );
}
